import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { USER_TYPE } from '../../../../../utils/constants/user';
import CategorySelector from '../../../../../common/CategorySelector/CategorySelector';
import DealsTable from './DealsTable/DealsTable';
import EmptyDeals from '../EmptyDeals/EmptyDeals';
import LoadingSpinner from '../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import SearchBox from '../../../Portfolio/components/CompaniesList/components/SearchBox/SearchBox';
import styles from './DealsList.module.scss';
import { useDealsList } from './useDealsList';
import { Button } from '../../../../../common/_custom';
import ColumnCustomizationButton from '../../../../../common/_custom/ColumnCustomizationButton';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { setCustomFilterDrawer, setColumnCustomizationState } from '../../../../../store/investor/action';
import { debtProdFilters, GREY_DROPDOWN_CARET, PURPLE_CROSS } from '../../../../../assets/hostedassets';
import { isAppliedFiltersEmpty, isLenderType } from '../../../../../utils/utils';
import { useParams } from 'react-router-dom';
import { DealStatus } from '../../../../../store/investor/reducer';
import { COLORS } from '../../../../../utils/constants/colors';
import FilteredViewsButton from '../../../../../common/FilteredViewsButton/FilteredViewsButton';
import useClearFilteredView from '../../../../../common/FilteredViewsButton/useClearFilteredView';
import { useIsMobile } from '../../../../../common/_custom/Hooks/useIsMobile';
import MobileHeader from '../../../../../common/MobileLayout/MobileHeader';
import MobileBottomTabs from '../../../../../common/MobileLayout/MobileBottomTabs';
import MobileViewSheet from '../../../Portfolio/components/CompaniesList/components/MobileViewSheet/MobileViewSheet';
import DealCardList from './components/DealCard/DealCardList';
// DataTableFilterDrawer is rendered by the hidden DealsTable below
import { DEAL_VIEW_TYPES } from '../../../../../store/investor/constants';
import { VIEW_CONFIGS } from '../../../../../utils/constants/commonConstants';
import { getDealFormConfig } from '../../../../../store/investor/apiHelpers';

export const fallbackDealStatusOption = {
    [DealStatus.SHARED]: {
        label: 'Deal Shared',
        value: DealStatus.SHARED,
        backgroundColor: COLORS.PURPLE_TINT_50,
        labelColor: 'var(--primary-text-colour)',
        customCaret: {
            height: '14',
            width: '14',
            colour: 'var(--primary-text-colour)',
        },
    },
    Default: {
        label: '-',
        value: '',
        backgroundColor: 'transparent',
        labelColor: '#000',
        customCaret: {
            height: '14',
            width: '14',
            colour: '#000',
        },
    },
    Select: {
        label: 'Select',
        value: '-',
        backgroundColor: COLORS.LIGHT_BG,
        labelColor: COLORS.GREY,
        customCaret: GREY_DROPDOWN_CARET,
    },
};

const DealsList: React.FC<any> = ({ selectedCompany }) => {
    const {
        fetchingDeals,
        dealsData,
        userType,
        displayedOrgList,
        selectedCategory,
        selectedOrgId,
        selectedLenderId,
        accessTierParam,
        LENGTH,
        CATEGORY_OPTIONS,
        setScrollPos,
        getTotalCount,
        changeSelectedTab,
        changePageNumber,
        handleSearch,
        handleOrgSelect,
        redirectToPreferences,
        sortConfig,
        handleSort,
        appliedFilters,
        currentColumns,
        dealsColumnsConfig,
        setDealsColumnsConfig,
    } = useDealsList();

    // Get the correct column configuration based on selected category
    const getMasterColumnConfig = () => {
        if (!dealsColumnsConfig) return [];

        if (selectedCategory === 'DEAL_VIEW') {
            return dealsColumnsConfig.dealViewHeaders || [];
        } else if (selectedCategory === 'MANDATE_VIEW') {
            return dealsColumnsConfig.mandateHeaders || [];
        } else {
            // Default to deal view headers
            return dealsColumnsConfig.dealViewHeaders || [];
        }
    };

    const LoadingTableData = <LoadingSpinner color="var(--primary-text-colour)" height="40px" />;

    const dispatch = useAppDispatch();
    // Mobile header/toolbar refs for the scroll-border observer below
    const headerSentinelRef = useRef<HTMLDivElement>(null);
    const mobileTopRef = useRef<HTMLDivElement>(null);
    const params = useParams();
    const isCompanyDashboardDealsPage = !!params.id;

    // Check if user is Lender or Partner (should not see Select View functionality)
    const isLenderUser = isLenderType(userType);
    const isPartnerUser = userType === USER_TYPE.PARTNER;
    const isCompanyUser = userType === USER_TYPE.COMPANY;

    const shouldHideViewSelector = isLenderUser || isPartnerUser;

    // Use the custom hook for clearing filtered views
    const { clearFilteredView } = useClearFilteredView({
        source: 'DEALS',
        filterSourceKey: 'DEALS',
    });

    const customFilterDrawer = useAppSelector((state) => state.investor.customFilterDrawer);

    const isMobile = useIsMobile();
    const [selectedMobileView, setSelectedMobileView] = useState<any>(null);
    const hasActiveFilters = !isAppliedFiltersEmpty(appliedFilters);
    const userData = useAppSelector((state) => state.user.userData);
    const platformData = useAppSelector((state) => state.user.domainMetadata);

    // Fetch deal columns config on mobile (desktop gets it from DealsTable).
    // Fetch-once: `dealsColumnsConfig` is read as a guard but intentionally kept
    // out of the dep array so setting it doesn't retrigger the effect.
    useEffect(() => {
        if (!isMobile || dealsColumnsConfig || !platformData?.investorOrgId) return;
        getDealFormConfig(
            {
                accountType: userData?.accountType,
                investorOrgId: platformData?.investorOrgId,
                primaryEmail: userData?.primaryEmail,
                configName: VIEW_CONFIGS.AICA_DEAL_NAVBAR_VIEW,
            },
            {
                onSuccess: (configData: any) => {
                    setDealsColumnsConfig(configData);
                },
                onError: () => {},
            },
        ).catch(() => {});
    }, [isMobile, platformData?.investorOrgId]);

    const clearAllFiltersAndView = useCallback(() => {
        clearFilteredView();
        setSelectedMobileView(null);
        dispatch(
            setCustomFilterDrawer({
                open: false,
                appliedFilters: { DEALS: {} },
            }),
        );
    }, [clearFilteredView, dispatch]);

    const handleViewSelect = useCallback(
        (filters: any, columns?: string[]) => {
            const sharedSourceKey = 'DEALS';
            dispatch(
                setColumnCustomizationState({
                    sourceKey: sharedSourceKey,
                    selectedColumns: columns && columns.length > 0 ? columns : [],
                    tempSelectedColumns: columns && columns.length > 0 ? columns : [],
                    isModified: false,
                }),
            );
        },
        [dispatch],
    );

    const cardListKey = useMemo(
        () =>
            `${selectedCategory}-${JSON.stringify(appliedFilters)}-${selectedMobileView?.value || ''}-${selectedOrgId || ''}-${
                selectedLenderId || ''
            }`,
        [selectedCategory, appliedFilters, selectedMobileView?.value, selectedOrgId, selectedLenderId],
    );

    const NoResultComponent = (
        <EmptyDeals
            handleClick={redirectToPreferences}
            selectedCompany={selectedCompany}
            filterApplied={!isAppliedFiltersEmpty(appliedFilters)}
            onClearFilters={clearAllFiltersAndView}
        />
    );
    // On mobile, always start from page 1 — infinite scroll handles pagination locally.
    // Without this, a refresh keeps the stale ?page=N param and fetches a mid-list page.
    useEffect(() => {
        if (!isMobile) return;
        changePageNumber(1);
    }, [isMobile]);

    useEffect(() => {
        if (!isMobile) return;
        const sentinel = headerSentinelRef.current;
        const toolbar = mobileTopRef.current;
        if (!sentinel || !toolbar) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                toolbar.style.borderBottomColor = entry.isIntersecting ? 'transparent' : 'var(--border-color-new)';
            },
            { threshold: 0 },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [isMobile]);

    if (isMobile) {
        return (
            <div className={styles.DealsList}>
                <MobileHeader />
                <div ref={headerSentinelRef} />
                <div ref={mobileTopRef} className={styles.MobileTop}>
                    <div className={styles.MobileTopRow}>
                        <div className={styles.Label}>{isCompanyUser ? 'Loans' : 'Deals'}</div>
                        <div className={styles.MobileActions}>
                            {!shouldHideViewSelector && (
                                <MobileViewSheet
                                    source={selectedCategory}
                                    selectedView={selectedMobileView}
                                    onViewSelect={handleViewSelect}
                                    onSelectedViewChange={setSelectedMobileView}
                                    onFiltersApply={(filters) => {
                                        const sourceKey = 'DEALS';
                                        dispatch(
                                            setCustomFilterDrawer({
                                                open: false,
                                                appliedFilters: {
                                                    [sourceKey]: filters,
                                                },
                                            }),
                                        );
                                    }}
                                    onFilterValuesUpdate={(filters) => {
                                        const sourceKey = 'DEALS';
                                        dispatch(
                                            setCustomFilterDrawer({
                                                open: false,
                                                appliedFilters: {
                                                    [sourceKey]: filters,
                                                },
                                            }),
                                        );
                                    }}
                                    onClear={clearAllFiltersAndView}
                                />
                            )}
                            <Button
                                text={''}
                                onClick={() => {
                                    dispatch(setCustomFilterDrawer({ open: true }));
                                }}
                                icon={debtProdFilters}
                                style={{
                                    marginTop: 0,
                                    borderRadius: '0.25rem',
                                    height: '2.5rem',
                                    width: '2.5rem',
                                    minWidth: '2.5rem',
                                    padding: '0',
                                    gap: '0',
                                    ...(hasActiveFilters
                                        ? {
                                              background: 'var(--primary-purple-hover-bg)',
                                              border: '1px solid var(--primary-purple-hover-bg)',
                                              width: 'auto',
                                              minWidth: 'auto',
                                              padding: undefined,
                                              gap: '0.375rem',
                                          }
                                        : {}),
                                }}
                                variant={'secondary'}
                                endIcon={hasActiveFilters ? PURPLE_CROSS : null}
                                endIconStyles={{ height: '0.8rem', marginLeft: '0.25rem' }}
                                iconStyles={
                                    hasActiveFilters
                                        ? { paddingRight: '0.5rem', borderRight: '1px solid var(--border-color)', width: '1.5rem' }
                                        : {
                                              margin: '0 auto',
                                              display: 'block',
                                              filter: 'brightness(0) saturate(100%) invert(29%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%)',
                                          }
                                }
                                endIconClick={clearAllFiltersAndView}
                            />
                            <SearchBox
                                onSearch={handleSearch}
                                categoryParam={''}
                                location={location}
                                accessTierParam={accessTierParam || ''}
                                placeholder="Search"
                                showDropdown={true}
                                dropdownOptions={displayedOrgList.map((org) => ({
                                    id: org.assesseeOrgId,
                                    label: org.orgName,
                                }))}
                                onOptionSelect={handleOrgSelect}
                            />
                        </div>
                    </div>
                    <div className={styles.MobileCategoryTabs}>
                        <CategorySelector
                            categoryOptions={CATEGORY_OPTIONS}
                            selectedCategory={selectedCategory}
                            changeSelectedTab={changeSelectedTab}
                        />
                    </div>
                </div>

                <DealCardList
                    key={cardListKey}
                    deals={dealsData.data || []}
                    isLoading={fetchingDeals}
                    hasNextPage={dealsData.hasNextPage}
                    dealStatusConfig={dealsData.dealStatusConfig}
                    selectedCategory={selectedCategory}
                    onLoadMore={() => changePageNumber(dealsData.pageNumber + 1)}
                    emptyComponent={NoResultComponent}
                />

                <MobileBottomTabs />

                {/* DealsTable rendered off-screen so its DataTableFilterDrawer works.
                    SideDrawer inside uses position:fixed, escaping this container. */}
                <div className={styles.OffscreenTable}>
                    <DealsTable
                        isLoading={false}
                        emptyScreenComponent={null}
                        loadingComponent={null}
                        setScrollPos={() => {}}
                        selectedCategory={selectedCategory}
                        sortConfig={sortConfig}
                        onSort={() => {}}
                        currentColumns={currentColumns}
                        onColumnsChange={() => {}}
                        onColumnsConfigChange={(config) => setDealsColumnsConfig(config)}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.DealsList}>
            <div className={styles.Top}>
                <div className={styles.LeftSection}>
                    <div className={styles.SectionTitle}>{isCompanyUser ? 'Loans' : 'Deals'}</div>
                    {![USER_TYPE.LENDER, USER_TYPE.LENDER_TEAM].includes(userType) && (
                        <div className={styles.CategorySelector}>
                            <CategorySelector
                                categoryOptions={CATEGORY_OPTIONS}
                                selectedCategory={selectedCategory}
                                changeSelectedTab={changeSelectedTab}
                            />
                        </div>
                    )}
                </div>
                <div className={styles.RightSection}>
                    {!isLenderType(userType) && !isCompanyDashboardDealsPage && !isCompanyUser && (
                        <SearchBox
                            onSearch={handleSearch}
                            categoryParam={''}
                            location={location}
                            accessTierParam={accessTierParam || ''}
                            showDropdown={true}
                            dropdownOptions={displayedOrgList.map((org) => ({
                                id: org.assesseeOrgId,
                                label: org.orgName,
                            }))}
                            onOptionSelect={handleOrgSelect}
                        />
                    )}
                    <FilteredViewsButton
                        source={selectedCategory}
                        appliedFilters={appliedFilters}
                        currentColumns={currentColumns}
                        onViewSelect={handleViewSelect}
                        masterColumnConfig={getMasterColumnConfig()}
                    />
                    <Button
                        text={''}
                        onClick={() => {
                            dispatch(
                                setCustomFilterDrawer({
                                    open: true,
                                }),
                            );
                        }}
                        icon={debtProdFilters}
                        style={{
                            marginTop: 0,
                            borderRadius: '0.25rem',
                            height: '2.75rem',
                            ...(isAppliedFiltersEmpty(appliedFilters)
                                ? {}
                                : {
                                      background: '#F2E8FF',
                                      border: '1px solid #F2E8FF',
                                  }),
                        }}
                        variant={'purpleinverted'}
                        iconStyles={
                            !isAppliedFiltersEmpty(appliedFilters)
                                ? { paddingRight: '0.5rem', borderRight: '1px solid #C7C7C7', width: '1.5rem' }
                                : { margin: '0' }
                        }
                        endIcon={!isAppliedFiltersEmpty(appliedFilters) ? PURPLE_CROSS : null}
                        endIconClick={() => {
                            // Clear filters and filtered view using the custom hook
                            clearFilteredView();
                        }}
                    />
                    <ColumnCustomizationButton
                        enabled={true}
                        filterSourceKey="DEALS"
                        currentColumns={currentColumns}
                        areColumnsCustomized={currentColumns.length > 0}
                        variant="secondary"
                        onCustomColumnsCleared={() => {
                            // Update Redux state to clear column customization
                            dispatch(
                                setColumnCustomizationState({
                                    sourceKey: 'DEALS',
                                    selectedColumns: [],
                                    tempSelectedColumns: [],
                                    isModified: false,
                                }),
                            );
                        }}
                        masterColumnConfig={getMasterColumnConfig()}
                    />
                </div>
            </div>
            <div className={styles.TableContainer}>
                <DealsTable
                    isLoading={fetchingDeals}
                    emptyScreenComponent={NoResultComponent}
                    loadingComponent={LoadingTableData}
                    setScrollPos={setScrollPos}
                    selectedCategory={selectedCategory}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    currentColumns={currentColumns}
                    onColumnsChange={(columns) => {
                        // Update Redux state for column customization
                        dispatch(
                            setColumnCustomizationState({
                                sourceKey: 'DEALS',
                                selectedColumns: columns.map((col) => col.field),
                                tempSelectedColumns: columns.map((col) => col.field),
                                isModified: false,
                            }),
                        );
                    }}
                    onColumnsConfigChange={(config) => {
                        setDealsColumnsConfig(config);
                    }}
                />

                <div className={styles.PageNumber}>
                    <div>
                        {`Showing ${getTotalCount() > 0 && dealsData?.pageNumber > 0 ? LENGTH * (dealsData.pageNumber - 1) + 1 : 0} - ${
                            getTotalCount() > 0 && dealsData?.pageNumber > 0 ? Math.min(LENGTH * dealsData.pageNumber, getTotalCount()) : 0
                        } of ${getTotalCount() || 0}`}
                    </div>

                    <div>
                        <img src="https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/poweredByAica.svg" alt="" width={'150px'} />
                    </div>
                    <div className={styles.Buttons}>
                        <div
                            className={clsx(styles.Btn, {
                                [styles.Disabled]: !(dealsData.pageNumber > 1),
                            })}
                            onClick={() => {
                                changePageNumber(dealsData.pageNumber - 1);
                            }}
                        >
                            Previous
                        </div>
                        <div
                            className={clsx(styles.Btn, {
                                [styles.Disabled]: !dealsData?.hasNextPage,
                            })}
                            onClick={() => {
                                changePageNumber(dealsData.pageNumber + 1);
                            }}
                        >
                            Next
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DealsList;
